import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
// import { TABLE_CODEBOOKS_PARKING, TABLE_CODEBOOKS_RIDE } from '../../Services/TableService';
import ThSearch from '../../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../../CmdTable/types';
import DateTimeCell from '../../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../../CmdTable/TableHeadCell/ThOnlySort';
import ThMultiSelect from '../../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../../CmdTable/TableCells/TextCell';
import { BluecoinRecord } from './BluecoinsListState';
import {
  ASSIGNED_KEY,
  BLE_TABLE_NAME,
  CHECKBOX_KEY,
  LAST_USAGE_ADDRESS_KEY,
  LAST_USAGE_KEY,
  MAC_ADDRESS_KEY,
  MANUFACTURER_KEY,
  MODEL_KEY,
  SERIAL_NUMBER_KEY,
  STATE_KEY,
  USAGE_START_ADDRESS_KEY,
  USAGE_START_KEY,
  UUID_KEY,
  VEHICLE_KEY,
  VEHICLE_LICENCE_NUMBER_KEY,
} from '../constants';
import ThText from '../../CmdTable/TableHeadCell/ThText';
import LinkCell from '../../CmdTable/TableCells/LinkCell';
import { ACTIONS_KEY, CUSTOMERS_KEY } from '../../CmdTable/constants';
import CustomersCell from '../../CmdTable/TableCells/CustomersCell';
import Actions from './RowActions/Actions';
import IndeterminateCheckbox from '../../CmdTable/IndeterminateCheckbox';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { columnsAtom, filterForRequestAtomFamily, sortFilterAtomFamily } from '../../CmdTable/CmdTableState';

interface IUseBleList {
  columns: ColumnDef<BluecoinRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useBleList(): IUseBleList {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  // const tableFiltersKey = useRecoilValue(CmdTableState.tableFilters);
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
  filterForRequestAtomFamily(BLE_TABLE_NAME)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(BLE_TABLE_NAME)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  // const getSelectedRows = () => {
  //   const selectedRows = table.getSelectedRowModel().rows;
  //   console.log('Selected Rows:', selectedRows);
  //   return selectedRows;
  // };

  const handleIndeterminateChange = (table) => {
    if (table.getIsSomeRowsSelected()) {
      table.toggleAllRowsSelected(false);
    } else {
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
    }
  };

  const handleCheckboxChange = (event, row) => {
    row.getToggleSelectedHandler()(event);
  };

  // const handleCheckedCheckboxChange = (row) => {
  //   console.log('handleCheckedCheckboxChange', row, row.getIsSelected());
  //   row.getIsSelected();
  // };

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [BLE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  // const handleRemove = React.useCallback(
  //   async (data: BluecoinRecord) => {
  //     const ids = { [data.customerId.id]: [data.id] };
  //     const response: AxiosResponse | false = await putRequest(`/v1/${TABLE_CODEBOOKS_RIDE}/remove`, { ids }, { returnAxiosResponse: true });
  //     if (response && response.data && response.data.status === 'ok') {
  //       updateTable();
  //     }
  //   },
  //   [updateTable]
  // );

  // const handleAction = (data: BluecoinRecord) => {
  //   navigate(
  //     `${URL_CODEBOOKS}/${TABLE_CODEBOOKS_RIDE}/edit/${data.id}?customerId=${data.customerId.id}`
  //   );
  // };

  const columns = React.useMemo<ColumnDef<BluecoinRecord>[]>(
    () => [
      {
        id: CHECKBOX_KEY,
        header: ({ table }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkallrecords"
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={() => handleIndeterminateChange(table)}
              className="custom-checkbox"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkOneRecord"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={(event) => handleCheckboxChange(event, row)}
              className="custom-checkbox"
            />
          </div>
        ),
        size: 40,
        enablePinning: true,
      },
      {
        accessorKey: UUID_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_TABLE_NAME}
            id={UUID_KEY}
            title={f({ id: `table_list.columns.bluecoin.${UUID_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${UUID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.uuid ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 180,
      },
      {
        accessorKey: MAC_ADDRESS_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_TABLE_NAME}
            id={MAC_ADDRESS_KEY}
            title={f({ id: `table_list.columns.bluecoin.${MAC_ADDRESS_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${MAC_ADDRESS_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.macAddress ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        // size: 260,
        minSize: 260,
      },
      {
        accessorKey: SERIAL_NUMBER_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_TABLE_NAME}
            id={SERIAL_NUMBER_KEY}
            title={f({ id: `table_list.columns.bluecoin.${SERIAL_NUMBER_KEY}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.serialNumber ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 80,
      },
      {
        accessorKey: MANUFACTURER_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_TABLE_NAME}
            id={MANUFACTURER_KEY}
            title={f({ id: `table_list.columns.bluecoin.${MANUFACTURER_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${MANUFACTURER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.manufacturer ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 100,
      },
      {
        accessorKey: MODEL_KEY,
        header: (table) => (
          <ThSearch
            tableName={BLE_TABLE_NAME}
            id={MODEL_KEY}
            title={f({ id: `table_list.columns.bluecoin.${MODEL_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${MODEL_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.model ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: ASSIGNED_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={BLE_TABLE_NAME}
            id={ASSIGNED_KEY}
            title={f({ id: `table_list.columns.bluecoin.${ASSIGNED_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${ASSIGNED_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.assigned ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: STATE_KEY,
        header: (table) => (
          <ThMultiSelect
            tableName={BLE_TABLE_NAME}
            id={STATE_KEY}
            title={f({ id: `table_list.columns.bluecoin.${STATE_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${STATE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.state ? (
            <TextCell
              text={f({ id: `bluecoin.state.${info.getValue<string>()}` })}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_KEY,
        header: (table) => (
          <ThMultiSelect
            id={VEHICLE_KEY}
            tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${VEHICLE_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${VEHICLE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicle ? (
            <TextCell
              text={
                info.getValue<{
                  id: number | null;
                  licenceNumber: string | null;
                  name: string | null;
                }>()?.name || ''
              }
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: VEHICLE_LICENCE_NUMBER_KEY,
        header: (table) => (
          <ThMultiSelect
            id={VEHICLE_LICENCE_NUMBER_KEY}
            tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${VEHICLE_LICENCE_NUMBER_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${VEHICLE_LICENCE_NUMBER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicle ? (
            <TextCell text={info.row.original?.vehicle.licenceNumber || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: USAGE_START_KEY,
        header: (table) => (
          <ThDateTime
            id={USAGE_START_KEY}
            tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${USAGE_START_KEY}` })}
            tooltip={f({ id: `table_list.columns.bluecoin.${USAGE_START_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.usageStart ? (
            <DateTimeCell
              datetime={
                info.getValue<{
                  timestamp: string | null;
                  address: string | null;
                  addressLink: string | null;
                  coordinates: string | null;
                  coordinatesLink: string | null;
                }>().timestamp || ''
              }
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: LAST_USAGE_KEY,
        header: (table) => (
          <ThDateTime
            id={LAST_USAGE_KEY}
            tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${LAST_USAGE_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${LAST_USAGE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.lastUsage ? (
            <DateTimeCell
              datetime={
                info.getValue<{
                  timestamp: string | null;
                  address: string | null;
                  addressLink: string | null;
                  coordinates: string | null;
                  coordinatesLink: string | null;
                }>().timestamp || ''
              }
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: USAGE_START_ADDRESS_KEY,
        header: (table) => (
          <ThText
            // id={USAGE_START_ADDRESS_KEY}
            // tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${USAGE_START_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${USAGE_START_ADDRESS_KEY}.tooltip` })}
          />
        ),
        cell: (info) =>
          info.row.original?.usageStart ? (
            <LinkCell
              text={info.row.original?.usageStart?.address || ''}
              link={info.row.original?.usageStart?.addressLink || ''}
              // tooltipEnabled={!!info.row.original?.usageStart?.address}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 260,
      },
      {
        accessorKey: LAST_USAGE_ADDRESS_KEY,
        header: (table) => (
          <ThText
            // id={LAST_USAGE_ADDRESS_KEY}
            // tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${LAST_USAGE_ADDRESS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${LAST_USAGE_ADDRESS_KEY}.tooltip` })}
          />
        ),
        cell: (info) =>
          info.row.original?.lastUsage ? (
            <LinkCell
              text={info.row.original?.lastUsage?.address || ''}
              link={info.row.original?.lastUsage?.addressLink || ''}
              // tooltipEnabled={!!info.row.original?.lastUsage?.address}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 260,
      },
      {
        accessorKey: CUSTOMERS_KEY,
        header: (table) => (
          <ThOnlySort
            id={CUSTOMERS_KEY}
            tableName={BLE_TABLE_NAME}
            title={f({ id: `table_list.columns.bluecoin.${CUSTOMERS_KEY}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${CUSTOMERS_KEY}.tooltip` })}
            isSortable={false}
          />
        ),
        cell: (info) =>
          info.row.original?.customers ? (
            <CustomersCell
              customers={
                info.getValue<
                  {
                    id: number;
                    name: string;
                    label: string | null;
                  }[]
                >() || ''
              }
              tooltipEnabled={true}
            />
          ) : (
            <TextCell text="" />
          ),
        size: 260,
      },

      {
        accessorKey: ACTIONS_KEY,
        header: () => (
          <div className=" th-sticky sticky-col detail">{f({ id: 'table.actionsBtn' })}</div>
        ),
        cell: (info) =>
          info.row.original ? (
            <Actions<BluecoinRecord> data={info.row.original} updateTable={updateTable} />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f]
  );

  const columnOrder = React.useMemo(() => {
    const order = [CHECKBOX_KEY, ...columnsTable.map((item) => item.key)];
    order.push(ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
